/**
 * Module block management for the docx-editor.dev integration.
 *
 * Strategy:
 *   1. `initModuleBlock(view)` finds the "Thank you for…" / "Yours
 *      sincerely…" anchor paragraphs in the loaded template, deletes
 *      whatever was between them, and records that empty range as the
 *      "module block" in plugin state.
 *   2. `setModuleValues(view, modules)` rebuilds the module block with
 *      ONLY the modules that have non-empty content — one bold heading
 *      paragraph + content paragraph + spacer per filled module. Called
 *      whenever the user's textareas or radio selections change.
 *
 * Why rebuild instead of in-place replace: production templates don't
 * author `{{module_name}}` tokens for every module (only the case-context
 * fields like `{{CLAIMANT_NAME}}`). The previous TipTap Split used an HTML
 * splice between the same two anchors to render module slots; this is the
 * docx-editor analogue.
 *
 * Trade-off: rebuild loses the cursor in the editor on every textarea
 * keystroke (debounced 250ms). Acceptable for a left-pane-driven flow;
 * worth revisiting if direct editor editing becomes the primary use case.
 */
import {
  Fragment,
  type Mark,
  DOMParser as PMDOMParser,
  type Node as PMNode,
  type ResolvedPos,
  type Schema,
  Slice,
} from "prosemirror-model";
import { type EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";

import {
  buildInlineNodes,
  firstRunMarks,
  fontMarksOf,
  looksLikeVisualHeading,
  normalizePastedParagraphs,
  remapFontMarks,
  stripColorMarks,
  stripFontMarks,
  visualParaAttrs,
} from "./para-clone";
import { stripHtmlToText } from "./utils";

export type SlotRange = { from: number; to: number };

export type ModuleContent = {
  /** DB module name — used as the canonical key (lowercased internally). */
  name: string;
  /** Human-readable heading shown bold above the content. */
  label: string;
  /** Plain-text content; `\n` becomes a hardBreak. */
  value: string;
};

type SlotState = {
  /** Per-module slot ranges (typically one each, but multi-occurrence is
   *  preserved in case a future template references the same module twice). */
  ranges: Map<string, SlotRange[]>;
  /** Module currently being edited from the left pane — gets a soft highlight. */
  activeName: string | null;
  /** Bounds of the dynamic module-block area within the doc. */
  moduleBlock: SlotRange | null;
};

type SlotMeta =
  | {
      type: "rebuild";
      moduleBlock: SlotRange;
      ranges: Map<string, SlotRange[]>;
    }
  | { type: "setActive"; name: string | null };

const ACTIVE_SLOT_STYLE =
  "background-color: rgba(250, 204, 21, 0.22); border-radius: 2px;";

export const slotPluginKey = new PluginKey<SlotState>("moduleSlot");

/** ProseMirror plugin that tracks the module block bounds and per-module
 *  content ranges, mapping them through every transaction so positions
 *  stay valid after unrelated edits. */
export const slotPlugin = new Plugin<SlotState>({
  key: slotPluginKey,
  state: {
    init: () => ({ ranges: new Map(), activeName: null, moduleBlock: null }),
    apply: (tr, prev) => {
      const ranges = new Map<string, SlotRange[]>();
      for (const [name, list] of prev.ranges) {
        const mapped: SlotRange[] = [];
        for (const range of list) {
          const from = tr.mapping.map(range.from, 1);
          const to = tr.mapping.map(range.to, -1);
          if (to >= from) mapped.push({ from, to });
        }
        if (mapped.length > 0) ranges.set(name, mapped);
      }
      let moduleBlock = prev.moduleBlock
        ? {
            from: tr.mapping.map(prev.moduleBlock.from, 1),
            to: tr.mapping.map(prev.moduleBlock.to, -1),
          }
        : null;
      let activeName = prev.activeName;

      const meta = tr.getMeta(slotPluginKey) as SlotMeta | undefined;
      if (meta?.type === "rebuild") {
        moduleBlock = meta.moduleBlock;
        ranges.clear();
        for (const [n, list] of meta.ranges) {
          ranges.set(n, list);
        }
      } else if (meta?.type === "setActive") {
        activeName = meta.name;
      }
      return { ranges, activeName, moduleBlock };
    },
  },
  props: {
    decorations(state) {
      const slot = slotPluginKey.getState(state);
      if (!slot?.activeName) return DecorationSet.empty;
      const ranges = slot.ranges.get(slot.activeName);
      if (!ranges || ranges.length === 0) return DecorationSet.empty;
      const decos = ranges.map((r) =>
        Decoration.inline(r.from, r.to, { style: ACTIVE_SLOT_STYLE })
      );
      return DecorationSet.create(state.doc, decos);
    },
  },
});

// ── Anchor + bounds setup ────────────────────────────────────────────────

/**
 * Locate the "Thank you for…" and "Yours sincerely…" paragraphs in the
 * doc, delete everything between them, and record an empty `moduleBlock`
 * range in plugin state at that position. Subsequent calls to
 * `setModuleValues` fill that range with rendered modules. Idempotent
 * via state.moduleBlock — calling twice is a no-op.
 *
 * @returns true on success; false if the editor's schema lacks a
 *   `paragraph` node (defensive).
 */
export const initModuleBlock = (view: EditorView): boolean => {
  const state = slotPluginKey.getState(view.state);
  if (state?.moduleBlock) return true; // already initialised
  if (!view.state.schema.nodes.paragraph) return false;

  let thankYouEnd: number | null = null;
  let signatureStart: number | null = null;
  view.state.doc.descendants((node, pos) => {
    if (node.type.name !== "paragraph") return true;
    const text = node.textContent;
    if (thankYouEnd === null && /(thank you for|i write regarding)/i.test(text)) {
      thankYouEnd = pos + node.nodeSize;
    }
    if (signatureStart === null && /yours\s+(sincerely|faithfully)/i.test(text)) {
      signatureStart = pos;
    }
    return false;
  });

  let start: number;
  let end: number;
  if (
    thankYouEnd !== null &&
    signatureStart !== null &&
    signatureStart > thankYouEnd
  ) {
    start = thankYouEnd;
    end = signatureStart;
  } else if (thankYouEnd !== null) {
    start = thankYouEnd;
    end = thankYouEnd;
  } else {
    start = view.state.doc.content.size;
    end = view.state.doc.content.size;
  }

  const tr = view.state.tr.delete(start, end);
  tr.setMeta(slotPluginKey, {
    type: "rebuild",
    moduleBlock: { from: start, to: start },
    ranges: new Map(),
  });
  view.dispatch(tr);
  return true;
};

// ── Inline content building ──────────────────────────────────────────────

// ── Module block rebuild ─────────────────────────────────────────────────

/**
 * Replace the module block range with paragraphs rendered from `modules`.
 * Filters to only those with non-empty values — empty modules are absent
 * from the doc entirely. Each rendered module = bold heading paragraph +
 * content paragraph. An empty paragraph spacer is inserted between
 * adjacent modules for visual breathing room.
 *
 * @returns true if the doc was updated; false if `initModuleBlock` hasn't
 *   been called yet or the schema can't be built against.
 */
export const setModuleValues = (
  view: EditorView,
  modules: ModuleContent[]
): boolean => {
  const state = slotPluginKey.getState(view.state);
  if (!state?.moduleBlock) return false;

  const schema = view.state.schema;
  const paragraphType = schema.nodes.paragraph;
  if (!paragraphType) return false;
  const strongMark = schema.marks.strong ?? schema.marks.bold;

  const filled = modules.filter((m) => m.value.trim().length > 0);
  // MDRS-292: synthesised module content must match the template font, not the
  // fork's schema default. Read the prevailing run at the module block's anchor
  // and apply it — the content run gets the full donor style, the heading keeps
  // its bold plus the template font/size.
  const donorMarks = donorMarksAt(view.state, state.moduleBlock.from);
  const donorFontMarks = fontMarksOf(donorMarks);
  const headingMarks = strongMark
    ? [strongMark.create(), ...donorFontMarks]
    : donorFontMarks.length > 0
      ? donorFontMarks
      : undefined;

  const nodes: PMNode[] = [];
  const newRanges = new Map<string, SlotRange[]>();
  // Cursor walks the post-replace doc; everything starts at the block's
  // current `from` position (which has been mapped through any prior tx).
  let cursor = state.moduleBlock.from;

  // Leading spacer before the first module so it doesn't butt up against
  // the "Thank you for…" anchor paragraph.
  if (filled.length > 0) {
    const leadSpacer = paragraphType.create(null, Fragment.empty);
    nodes.push(leadSpacer);
    cursor += leadSpacer.nodeSize;
  }

  for (let i = 0; i < filled.length; i++) {
    const m = filled[i];

    // Heading paragraph
    const headingTextNode = m.label ? schema.text(m.label, headingMarks) : null;
    const headingPara = paragraphType.create(
      null,
      headingTextNode ? Fragment.from(headingTextNode) : Fragment.empty
    );
    nodes.push(headingPara);
    cursor += headingPara.nodeSize;

    // Content paragraph — record the inline content range for mirror-back.
    const contentFragment = Fragment.from(
      buildInlineNodes(schema, m.value, donorMarks)
    );
    const contentPara = paragraphType.create(null, contentFragment);
    const contentStart = cursor + 1; // skip open token
    const contentEnd = contentStart + contentFragment.size;
    newRanges.set(m.name.toLowerCase(), [{ from: contentStart, to: contentEnd }]);
    nodes.push(contentPara);
    cursor += contentPara.nodeSize;

    // Spacer after every module's content paragraph — including the
    // last one, so the "Yours faithfully," anchor below has breathing
    // room from the final module.
    const spacer = paragraphType.create(null, Fragment.empty);
    nodes.push(spacer);
    cursor += spacer.nodeSize;
  }

  const fragment = Fragment.from(nodes);
  const slice = new Slice(fragment, 0, 0);
  const newBlockEnd = state.moduleBlock.from + slice.content.size;

  try {
    const tr = view.state.tr.replace(
      state.moduleBlock.from,
      state.moduleBlock.to,
      slice
    );
    tr.setMeta(slotPluginKey, {
      type: "rebuild",
      moduleBlock: { from: state.moduleBlock.from, to: newBlockEnd },
      ranges: newRanges,
    });
    view.dispatch(tr);
    return true;
  } catch (err) {
    console.warn("[slots] setModuleValues failed:", err);
    return false;
  }
};

// ── Active slot decoration ───────────────────────────────────────────────

/** Set (or clear) the currently-active slot — drives the inline
 *  highlight decoration in the editor. */
export const setActiveSlot = (view: EditorView, name: string | null) => {
  const tr = view.state.tr.setMeta(slotPluginKey, {
    type: "setActive",
    name: name == null ? null : name.toLowerCase(),
  });
  view.dispatch(tr);
};

// ── Read-back helpers ────────────────────────────────────────────────────

/** Names of modules currently rendered in the doc (have a tracked range). */
export const listTrackedSlots = (view: EditorView): Set<string> => {
  const state = slotPluginKey.getState(view.state);
  return new Set(state?.ranges.keys() ?? []);
};

/** Read the current text content of every tracked slot, converting
 *  `hardBreak` nodes back to `\n`. Used by the mirror-back path so direct
 *  edits in the editor propagate back into the textareas. */
export const readTrackedSlots = (view: EditorView): Map<string, string> => {
  const state = slotPluginKey.getState(view.state);
  if (!state) return new Map();
  const out = new Map<string, string>();
  const leafText = (node: { type: { name: string } }) =>
    node.type.name === "hardBreak" ? "\n" : "";
  for (const [name, list] of state.ranges) {
    if (list.length === 0) continue;
    const first = list[0];
    const text = view.state.doc.textBetween(
      first.from,
      first.to,
      undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      leafText as any
    );
    out.set(name, text);
  }
  return out;
};

/** PM position for a module's slot, or the module block start if the
 *  module isn't currently rendered. Used to scroll the editor when the
 *  textarea gains focus. */
export const getSlotPosition = (view: EditorView, name: string): number | null => {
  const key = name.toLowerCase();
  const state = slotPluginKey.getState(view.state);
  const tracked = state?.ranges.get(key);
  if (tracked && tracked.length > 0) return tracked[0].from;
  if (state?.moduleBlock) return state.moduleBlock.from;
  return null;
};

/** Module block bounds, exposed for callers that need to know where the
 *  dynamic content area lives (currently unused but handy for debugging). */
export const getModuleBlock = (view: EditorView): SlotRange | null => {
  const state = slotPluginKey.getState(view.state);
  return state?.moduleBlock ?? null;
};

// ── Drop plugin ──────────────────────────────────────────────────────────

/** Walk the PM doc to find the open-token position of the paragraph node
 *  whose `paraId` matches. Callers add `1 + before.length` to compute
 *  inline positions inside the paragraph. Returns null if not found. */
export const findParagraphStart = (
  view: EditorView,
  paraId: string
): number | null => {
  let found: number | null = null;
  view.state.doc.descendants((node, pos) => {
    if (found !== null) return false;
    if (
      (node.type.name === "paragraph" || node.type.name === "doc_paragraph") &&
      node.attrs?.paraId === paraId
    ) {
      found = pos;
      return false;
    }
    return true;
  });
  return found;
};

/**
 * Backstop drop handler for text/html and text/plain payloads coming from
 * the AI snippet cards' DraggableSnippetBody. docx-editor wires its own
 * drop logic for embedded images; this plugin returns `true` from
 * handleDrop only when the dataTransfer contains text — letting image
 * drops fall through to docx-editor untouched.
 */
/**
 * Marks to apply to text inserted at `pos` so it blends with the surrounding
 * template body. Walks up to the nearest textblock and reads its first text
 * run's marks (font / size / weight); falls back to the marks active at the
 * cursor when the parent has no text yet. Without this the inserted text falls
 * back to the schema default (Calibri 11pt / "Normal text") while the rest of
 * the report uses the template's chosen font — QA reported this as a jarring
 * two-style paragraph on the Dr Sam Jones template.
 *
 * Exported so EditorOverlays' refine/revert/dictation actions (MDRS-264) can
 * apply the same treatment when they replace/insert text at a known position.
 *
 * `editingInPlace` distinguishes the two shapes that share this helper:
 *   - false (default) — inserting/pasting content that's new to this spot
 *     (paste, drop, module rebuild). A heading's marks at `pos` must NOT be
 *     trusted here, even if `pos` sits inside one, because the caret sharing
 *     the heading's paragraph doesn't mean the pasted text IS that heading
 *     (MDRS-300 — e.g. caret left inside/below "CODE OF CONDUCT").
 *   - true — refine/revert/dictation replacing the text that's *already at*
 *     `pos` (MDRS-264). Here the marks active at `pos` describe the content
 *     being replaced, heading-shaped or not, and must be preserved — there's
 *     nothing to "leak" since the source and destination are the same spot.
 */
/** Font marks derived from the enclosing paragraph's `defaultTextFormatting`.
 *
 *  `fontSize` is in half-points in both the attr and the mark, so it maps
 *  across directly. Returns `[]` when the paragraph declares no formatting, or
 *  when the schema has no font marks (the minimal test schemas). */
const declaredFontMarks = (
  state: EditorState,
  $pos: ResolvedPos
): readonly Mark[] => {
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const block = $pos.node(depth);
    if (!block.isTextblock) continue;
    const declared = block.attrs?.defaultTextFormatting as
      | { fontSize?: number; fontFamily?: { ascii?: string; hAnsi?: string } }
      | null
      | undefined;
    if (!declared) return [];
    const marks: Mark[] = [];
    const sizeType = state.schema.marks.fontSize;
    if (sizeType && typeof declared.fontSize === "number") {
      marks.push(sizeType.create({ size: declared.fontSize }));
    }
    const familyType = state.schema.marks.fontFamily;
    if (familyType && declared.fontFamily) {
      marks.push(familyType.create({ ...declared.fontFamily }));
    }
    return marks;
  }
  return [];
};

/** `primary` topped up with any font mark type it is missing from `fallback`.
 *
 *  A donor is used to REPLACE the paste's font marks wholesale
 *  (`remapFontMarks`), so an incomplete donor silently drops whatever it omits.
 *  A destination whose runs carry a fontFamily but no fontSize — size coming
 *  from the style — produced exactly that: the family transplanted correctly
 *  and the size fell back to the document default, rendering 11pt list items'
 *  pasted text at 16pt (MDRS-514). */
const completeFontMarks = (
  primary: readonly Mark[],
  fallback: readonly Mark[]
): Mark[] => {
  const have = new Set(primary.map((m) => m.type.name));
  return [...primary, ...fallback.filter((m) => !have.has(m.type.name))];
};

/** Font marks to transplant onto a rich paste.
 *
 *  Built from the destination's OWN formatting first — run marks, then the
 *  paragraph's declared formatting — topping up each font mark type that is
 *  still missing.
 *
 *  A neighbouring paragraph's marks (`donorMarksAt`'s last resort,
 *  `prevailingBodyMarks`) may contribute the FAMILY but never the SIZE.
 *  MDRS-292 added that fallback because bare text renders in the schema's
 *  default family (Calibri) rather than the template's, which is visible and
 *  wrong. Size has no such problem: unmarked text resolves through the same
 *  document defaults the destination itself renders with, so it already matches.
 *  Donating a neighbour's explicit size instead OVERRIDES that — a list item
 *  rendering at 11pt through defaults, next to a 16pt paragraph, received 16pt
 *  pasted text (MDRS-514). The destination in that report had no styleId, no
 *  defaultTextFormatting and no run marks at all, so the neighbour was the only
 *  donor and it was the wrong one. */
const pasteFontDonor = (state: EditorState, pos: number): Mark[] => {
  const $pos = state.doc.resolve(pos);
  const own = completeFontMarks(
    fontMarksOf(ownFontMarksAt(state, $pos)),
    declaredFontMarks(state, $pos)
  );
  const borrowed = fontMarksOf(donorMarksAt(state, pos)).filter(
    (m) => m.type.name !== "fontSize"
  );
  return completeFontMarks(own, borrowed);
};

/** Font marks the destination paragraph carries ITSELF — run marks or the marks
 *  active at the caret. Deliberately excludes `donorMarksAt`'s neighbour
 *  fallback; see `pasteFontDonor`. */
const ownFontMarksAt = (state: EditorState, $pos: ResolvedPos): readonly Mark[] => {
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const block = $pos.node(depth);
    if (!block.isTextblock) continue;
    const runMarks = firstRunMarks(block);
    if (runMarks.length > 0) return runMarks;
    break;
  }
  return $pos.marks();
};

export const donorMarksAt = (
  state: EditorState,
  pos: number,
  editingInPlace = false
): readonly Mark[] => {
  const $pos = state.doc.resolve(pos);
  let localBlockIsHeading = false;
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const block = $pos.node(depth);
    if (block.isTextblock) {
      // MDRS-451: `editingInPlace` normally trusts the marks at `pos` because
      // refine/revert replaces the *existing* text there (heading-shaped or
      // not). But dictation reuses `editingInPlace=true` while INSERTING into a
      // still-empty paragraph — and when the generator left that paragraph
      // heading-styled, the heading's bold/size then leaked into the dictated
      // body text. An empty heading block has no text being replaced, so treat
      // it like a fresh insert: skip its marks and fall back to the prevailing
      // body run below. A non-empty heading under refine/revert stays trusted.
      const blockIsEmpty = block.textContent.trim().length === 0;
      if (isHeadingBlock(block) && (!editingInPlace || blockIsEmpty)) {
        localBlockIsHeading = true;
      } else {
        const marks = firstRunMarks(block);
        if (marks.length > 0) return marks;
      }
      break;
    }
  }
  if (!localBlockIsHeading) {
    const active = $pos.marks();
    if (active.length > 0) return active;
  }
  // MDRS-514: the destination paragraph's own *declared* formatting, before
  // borrowing a distant paragraph's runs. Template-styled body text usually
  // carries its font on `defaultTextFormatting` rather than on explicit run
  // marks, so `firstRunMarks` above finds nothing and the fallback below would
  // hand back some other paragraph's size — pasting into an 11pt list item
  // rendered the pasted text at whatever the prevailing body run happened to
  // be (16pt, in the report this was found in). The paragraph's own formatting
  // is strictly more specific than another paragraph's.
  const declared = declaredFontMarks(state, $pos);
  if (declared.length > 0) return declared;
  // MDRS-292: the insertion point has no run of its own — the common case of
  // dictating/pasting into a freshly-created, still-empty section. Without a
  // local donor, inserted text would fall back to the fork's schema default
  // (Calibri 11) while the rest of the report uses the template font. Borrow
  // the document's prevailing body run instead so the new text blends in.
  return prevailingBodyMarks(state.doc, pos);
};

/** Heuristic: does this textblock render as a heading (so its weight/size
 *  shouldn't leak into a body insert)? OOXML heading paragraphs carry a
 *  `Heading`/`Title` styleId or an `outlineLevel`; some schemas also use a
 *  distinct node type. Falls back to the same ALLCAPS+every-run-bold visual
 *  check as `qta-insert.ts`'s `isHeadingPara` for the ~82% of templates that
 *  render headings as plain "Normal text" paragraphs with no Word heading
 *  style at all (MDRS-300) — without it, an empty body paragraph directly
 *  below one of these headings borrows the heading's bold via
 *  `prevailingBodyMarks` below. Best-effort — a false negative just means we
 *  might pick a slightly heavier donor, never a crash. */
const isHeadingBlock = (node: PMNode): boolean => {
  if (/^(heading|title)/i.test(node.type.name)) return true;
  const styleId = node.attrs?.styleId;
  // Anchored so a body style like "Subtitle" isn't mistaken for a heading.
  if (typeof styleId === "string" && /^(heading|title)/i.test(styleId)) {
    return true;
  }
  if (node.attrs?.outlineLevel != null) return true;
  const text = node.textContent.trim();
  if (!text) return false;
  return looksLikeVisualHeading(text, node);
};

/** Marks of the nearest non-empty *body* run in the document, used as the
 *  template's prevailing style when the insertion point itself has none.
 *  Prefers the closest body run to `pos`; falls back to the closest run of any
 *  kind (including headings) if the document has no body text, and to `[]` for
 *  a wholly empty document.
 *
 *  MDRS-300: when the `any` fallback's sole candidate is a heading — e.g. the
 *  doc so far is just "CODE OF CONDUCT" + its still-empty body paragraph,
 *  nothing else typed yet — its bold/strong must not ride along. Font family/
 *  size are still worth keeping (MDRS-292: any real font beats the schema
 *  default), so only the weight marks are stripped, not the whole run. */
const prevailingBodyMarks = (doc: PMNode, pos: number): readonly Mark[] => {
  let body: readonly Mark[] | null = null;
  let bodyDist = Infinity;
  let anyRun: readonly Mark[] | null = null;
  let anyDist = Infinity;
  let anyIsHeading = false;
  doc.descendants((node, nodePos) => {
    if (!node.isTextblock) return true;
    const marks = firstRunMarks(node);
    if (marks.length === 0) return false;
    const dist = Math.abs(nodePos - pos);
    if (dist < anyDist) {
      anyRun = marks;
      anyDist = dist;
      anyIsHeading = isHeadingBlock(node);
    }
    if (!isHeadingBlock(node) && dist < bodyDist) {
      body = marks;
      bodyDist = dist;
    }
    return false;
  });
  if (body) return body;
  if (anyRun === null) return [];
  const fallback: Mark[] = Array.from(anyRun);
  return anyIsHeading
    ? fallback.filter((m) => m.type.name !== "strong" && m.type.name !== "bold")
    : fallback;
};

export const dropPlugin = new Plugin({
  props: {
    handleDrop(view, event) {
      const dt = event.dataTransfer;
      if (!dt) return false;
      const html = dt.getData("text/html");
      const text = dt.getData("text/plain");
      if (!html && !text) return false;

      // Resolve the drop position from event coordinates.
      const coords = view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      });
      if (!coords) return false;

      const schema = view.state.schema;
      // Build inline content: split on \n, use hardBreak between lines.
      // text/plain is the source of truth here because docx-editor's
      // schema is OOXML and arbitrary text/html may not parse cleanly.
      // When text/plain is missing, fall back to DOMParser-based
      // text extraction (a regex strip is brittle — malformed tags slip
      // through and CodeQL flags it as incomplete sanitization, even
      // though the result is only used as ProseMirror text content).
      const source = text || stripHtmlToText(html);
      if (!source.trim()) return false;

      const donorMarks = donorMarksAt(view.state, coords.pos);
      const nodes: PMNode[] = buildInlineNodes(schema, source, donorMarks);
      if (nodes.length === 0) return false;

      const tr = view.state.tr.insert(coords.pos, Fragment.from(nodes));
      view.dispatch(tr);
      event.preventDefault();
      return true;
    },
  },
});

/** Structural list attrs — deliberately NOT part of `visualParaAttrs`.
 *
 *  `SAFE_PARA_ATTR_KEYS` excludes them because most synthesised paragraphs
 *  (module inserts, overlay inserts) must never inherit a neighbour's list
 *  membership. Paste is the exception: content dropped into a list should join
 *  that list. See `donorParaAttrsForPaste`. */
const LIST_PARA_ATTR_KEYS = [
  "numPr",
  "listIsBullet",
  "listNumFmt",
  "listMarker",
] as const;

/** Visual attrs of the paragraph enclosing `pos` — applied to synthesised
 *  paragraphs so they inherit the destination's style (see visualParaAttrs). */
const donorParaAttrs = (
  state: EditorState,
  pos: number
): Record<string, unknown> | null => {
  const $pos = state.doc.resolve(pos);
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const block = $pos.node(depth);
    if (block.type.name === "paragraph") return visualParaAttrs(block);
  }
  return null;
};

/** Donor attrs for a PASTE — the visual set plus the destination's list
 *  membership.
 *
 *  MDRS-514: `normalizePastedParagraphs` *replaces* each pasted paragraph's
 *  attrs with the donor's, so without the list keys the source's `numPr` is
 *  discarded and the destination's is never applied — pasting into a numbered
 *  list produced unnumbered body text mid-list.
 *
 *  The DESTINATION's list is the right one to inherit: pasting into a list
 *  continues that list rather than re-imposing the source's numbering, which
 *  is what Word does and what "paste and it just continues" means. When the
 *  caret isn't in a list the keys are absent, so nothing invents one. */
const donorParaAttrsForPaste = (
  state: EditorState,
  pos: number
): Record<string, unknown> | null => {
  const visual = donorParaAttrs(state, pos);
  const $pos = state.doc.resolve(pos);
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const block = $pos.node(depth);
    if (block.type.name !== "paragraph") continue;
    if (!block.attrs?.numPr) return visual;
    const listAttrs: Record<string, unknown> = {};
    for (const key of LIST_PARA_ATTR_KEYS) {
      if (key in block.attrs) listAttrs[key] = block.attrs[key];
    }
    return { ...(visual ?? {}), ...listAttrs };
  }
  return visual;
};

/** Parse a `text/html` clipboard string into a ProseMirror slice using the
 *  editor schema's own `parseDOM` rules, so bold/italic/lists/fonts land as the
 *  correct marks and nodes. Returns null if parsing throws. */
const parseHtmlToSlice = (schema: Schema, html: string): Slice | null => {
  try {
    const dom = new DOMParser().parseFromString(html, "text/html");
    return PMDOMParser.fromSchema(schema).parseSlice(dom.body);
  } catch {
    return null;
  }
};

/**
 * Paste companion to dropPlugin: re-apply the destination paragraph's marks so
 * pasted text inherits the template's font/size instead of the schema default
 * (Calibri 11 / "Normal text"), which otherwise produced a two-style paragraph
 * — e.g. the Code of Conduct copy button → Ctrl+V.
 *
 * SCOPE: only handles clipboards that carry NO usable `text/html`. The CoC copy
 * button writes plain text only, so it lands here; a rich paste from Word or
 * another editor still has `text/html` and falls through to docx-editor's
 * native paste, which preserves bold/italic/lists and real paragraph structure.
 * Returning `false` for those (and for image payloads) keeps that path intact.
 *
 * Blank-line-separated blocks (`\n\n`) become separate paragraph nodes so a
 * multi-paragraph declaration doesn't collapse into one; single `\n` within a
 * block stays a hardBreak (buildInlineNodes).
 */
export const pastePlugin = new Plugin({
  props: {
    handlePaste(view, event) {
      const dt = event.clipboardData;
      if (!dt) return false;

      const html = dt.getData("text/html");
      if (html.trim()) {
        // MDRS-292: rich clipboard — e.g. selecting rendered left-panel
        // content, or pasting from Word. Keep the source's structure and
        // formatting (bold/italic/lists) but swap its font family/size for the
        // template's, so pasted text matches the report font instead of
        // carrying the source's. Image/file pastes fall through to the fork's
        // native paste (which handles embedded media and preserves the source).
        if (dt.files && dt.files.length > 0) return false;
        // Inline images (data-URI or remote <img>) can arrive as text/html
        // with no dt.files entry — keep those on the fork's native media path.
        if (/<img[\s/>]/i.test(html)) return false;
        // Top the run-mark donor up from the destination paragraph's declared
        // formatting: `remapFontMarks` replaces the paste's font marks with
        // this set, so anything missing here is dropped from the result.
        const caretPos = view.state.selection.from;
        const donorFont = pasteFontDonor(view.state, caretPos);
        try {
          const parsed = parseHtmlToSlice(view.state.schema, html);
          if (!parsed || parsed.content.size === 0) return false;
          // When the template exposes an explicit font at the caret, transplant
          // it. Otherwise the template's font is the document default (theme
          // `minorHAnsi`, Calibri) which body runs carry implicitly — strip the
          // source's font so the paste inherits that default instead of falling
          // through to the fork's native paste, which keeps the source's font
          // (the Code-of-Conduct panel pastes as Times New Roman that way).
          let content =
            donorFont.length > 0
              ? remapFontMarks(parsed.content, donorFont)
              : stripFontMarks(parsed.content);
          // MDRS-403: match the report's own format, not the source's. Strip the
          // source colour/highlight (inherit the body colour) and rebuild each
          // pasted paragraph with the destination's spacing/alignment — dropping
          // any heading style — so paragraphs don't run together or render as a
          // copied heading. Genuine inline bold/italic in body text is preserved.
          content = stripColorMarks(content);
          content = normalizePastedParagraphs(
            content,
            donorParaAttrsForPaste(view.state, view.state.selection.from)
          );
          const remapped = new Slice(content, parsed.openStart, parsed.openEnd);
          const tr = view.state.tr.replaceSelection(remapped).scrollIntoView();
          view.dispatch(tr);
          event.preventDefault();
          return true;
        } catch {
          // A slice we can't fit at the caret (e.g. a Word table/nested list)
          // throws from replaceSelection — fall through to the fork's native
          // paste rather than dropping the paste entirely. (preventDefault has
          // NOT been called, so the browser/fork still handle the event.)
          return false;
        }
      }

      const text = dt.getData("text/plain");
      if (!text.trim()) return false;

      const { schema } = view.state;
      const paragraphType = schema.nodes.paragraph;
      if (!paragraphType) return false;

      const { from, to } = view.state.selection;
      const donorMarks = donorMarksAt(view.state, from);
      const donorAttrs = donorParaAttrsForPaste(view.state, from) ?? undefined;

      // Split on blank lines into paragraphs; each paragraph keeps soft line
      // breaks as hardBreaks. Drops empty blocks from runs of 3+ newlines.
      const blocks = text
        .split(/\n{2,}/)
        .map((b) => b.replace(/\n+$/, ""))
        .filter((b) => b.trim().length > 0);
      if (blocks.length === 0) return false;

      const paragraphs = blocks.map((block) =>
        paragraphType.create(donorAttrs, buildInlineNodes(schema, block, donorMarks))
      );

      // Open the slice one level at both ends so the first/last pasted
      // paragraphs merge into the paragraph the caret sits in (standard paste
      // behaviour) rather than splitting it into empty fragments.
      const slice = new Slice(Fragment.from(paragraphs), 1, 1);
      const tr = view.state.tr.replaceRange(from, to, slice);
      view.dispatch(tr.scrollIntoView());
      event.preventDefault();
      return true;
    },
  },
});
